import React, { useEffect, useState } from "react";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import { Field, Formik } from "formik";
import * as yup from "yup";
import "./styles.module.css";
import ConfirmationBox from "./ConfirmationBox.tsx";
import ClientSelectionFields from "./ClientSelectionFields.tsx";
import FreeAppointments from "./FreeAppointments.tsx";
import { madeList } from "./utlis.ts";
import UserService from "../services/user.service";
import DayOff from "./DayOff.tsx";
import DayOffRemove from "./DayOffRemove.tsx";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import type { AppointmentItem, AppointmentStatus, ClientSummary } from "../pages/models";

type SelectOption<T extends string | number = string> = {
  value: T;
  label: T | string;
};

type AppointmentRecord = {
  id?: string;
  startDateTime: string;
  endDateTime: string;
  status: AppointmentStatus;
  note?: string;
  mark?: string;
  model?: string;
  year?: number | string;
  client?: ClientSummary | null;
};

type AppointmentFormValues = {
  email: string;
  client_name: string;
  phone_number: string;
  client: ClientSummary | null;
  note: string;
  mark: SelectOption<string> | null;
  model: string;
  year: SelectOption<number | string> | null;
};

type AppointmentProps = {
  appointment: AppointmentRecord | null;
  show: boolean;
  onHide: () => void;
  onAppointmentChange: () => void;
};

type ApiError = {
  response?: {
    status?: number;
    data?: unknown;
  };
  message?: string;
};

const EMPTY_INITIAL_VALUES: AppointmentFormValues = {
  email: "",
  client_name: "",
  phone_number: "",
  client: null,
  note: "",
  mark: null,
  model: "",
  year: null,
};

const statusesAllowedToCancel: AppointmentStatus[] = ["added", "confirmed", "paid"];

const getClientPhoneNumber = (client?: Partial<ClientSummary> | null) =>
  client?.phoneNumber || client?.phone_number || "";

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiError;

  if (apiError.response?.status || apiError.response?.data) {
    return `${apiError.response.status ?? "Error"}: ${JSON.stringify(apiError.response.data)}`;
  }

  return apiError.message || "Unexpected error";
};

const getFieldError = (error: unknown, touched: unknown) =>
  Boolean(touched) && typeof error === "string" ? error : "";

const getProductionYears = (): SelectOption<number>[] => {
  const arrayOfYears: SelectOption<number>[] = [];
  let year = new Date().getFullYear();
  const month = new Date().getMonth();
  if (month > 9) {
    year += 1;
  }
  for (let currentYear = year; currentYear > 1970; currentYear -= 1) {
    arrayOfYears.push({
      value: currentYear,
      label: currentYear,
    });
  }
  return arrayOfYears;
};

const Appointment = ({ appointment, show, onHide, onAppointmentChange }: AppointmentProps) => {
  const [initialValues, setInitialValues] = useState<AppointmentFormValues>(EMPTY_INITIAL_VALUES);
  const [isEditDisabled, setIsEditDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editDate, setEditDate] = useState(false);
  const [isFreeAppointment, setIsFreeAppointment] = useState(false);
  const productionYears = getProductionYears();

  const validationSchema = yup.object().shape({
    client_name: yup
      .string()
      .required("Client name is required")
      .min(3, ({ min }) => `Client name must be at least ${min} characters`),
    email: yup
      .string()
      .email("Please enter valid e-mail")
      .required("E-mail is required"),
    phone_number: yup
      .string()
      .required("Phone number is required")
      .test("", "This not correct phone number", (value) =>
        isValidPhoneNumber(value || ""),
      ),
    mark: yup
      .object()
      .shape({
        value: yup.string(),
        label: yup.string(),
      })
      .nullable()
      .required("Mark is required"),
    year: yup
      .object()
      .shape({
        value: yup.mixed<string | number>(),
        label: yup.mixed<string | number>(),
      })
      .nullable()
      .required("Motorcycle year is required"),
  });

  useEffect(() => {
    const isFreeAppointmentValue = Boolean(
      appointment && ["draft", "day_off"].includes(appointment.status),
    );

    if (appointment && !isFreeAppointmentValue) {
      const client = appointment.client;
      setInitialValues({
        email: client?.email || "",
        client_name: client?.clientName || "",
        phone_number: getClientPhoneNumber(client),
        client: client || null,
        note: appointment.note || "",
        model: appointment.model || "",
        mark: appointment.mark
          ? {
              value: appointment.mark,
              label: appointment.mark,
            }
          : null,
        year: appointment.year
          ? {
              value: appointment.year,
              label: appointment.year,
            }
          : null,
      });
    } else {
      setInitialValues(EMPTY_INITIAL_VALUES);
    }

    setIsEditDisabled(Boolean(appointment && !isFreeAppointmentValue));
    setIsFreeAppointment(isFreeAppointmentValue);
  }, [appointment]);

  const formatDate = () => (appointment ? moment(appointment.startDateTime).format("LLL") : "");

  const editClient = async (values: AppointmentFormValues) => {
    try {
      const clientData = {
        clientName: values.client_name,
        email: values.email,
        phone_number: values.phone_number,
      };
      const clientId = appointment?.client?.id || values.client?.id;
      if (!clientId) {
        return undefined;
      }
      const response = await UserService.updateClient(clientId, clientData);
      return response.data.data.id as string | undefined;
    } catch (error) {
      alert(getApiErrorMessage(error));
      return undefined;
    }
  };

  const editAppointment = async (values: AppointmentFormValues) => {
    if (!appointment?.id) {
      return;
    }

    try {
      const appointmentData = {
        note: values.note,
        mark: values.mark?.value,
        model: values.model,
        year: values.year?.value,
      };
      await UserService.updateAppointment(appointment.id, appointmentData);
    } catch (error) {
      alert(getApiErrorMessage(error));
      console.log(error);
    }
  };

  const editAppointmentData = async (
    values: AppointmentFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setIsSubmitting(true);
    setSubmitting(true);

    try {
      const currentClient = appointment?.client;
      const hasClientChanged = Boolean(
        currentClient &&
          (currentClient.clientName !== values.client_name ||
            currentClient.email !== values.email ||
            getClientPhoneNumber(currentClient) !== values.phone_number),
      );
      const hasAppointmentChanged = Boolean(
        appointment &&
          (appointment.note !== values.note ||
            appointment.mark !== values.mark?.value ||
            appointment.model !== values.model ||
            appointment.year !== values.year?.value),
      );

      if (hasClientChanged) {
        await editClient(values);
        await editAppointment(values);
      } else if (hasAppointmentChanged) {
        await editAppointment(values);
      }
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  const addClientToDB = async (values: AppointmentFormValues) => {
    try {
      const clientData = {
        clientName: values.client_name,
        email: values.email,
        phone_number: values.phone_number,
      };
      const response = await UserService.postClient(clientData);
      return response.data.data.id as string | undefined;
    } catch (error) {
      alert(getApiErrorMessage(error));
      console.log(error);
      throw error;
    }
  };

  const addAppointmentForClient = async (
    values: AppointmentFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setSubmitting(true);

    try {
      let clientId: string | null | undefined = null;

      if (values.client) {
        const hasClientChanged =
          values.client.clientName !== values.client_name ||
          values.client.email !== values.email ||
          getClientPhoneNumber(values.client) !== values.phone_number;

        if (hasClientChanged) {
          await editClient(values);
        }

        clientId = values.client.id;
      } else if (appointment?.client) {
        clientId = appointment.client.id;
      } else {
        clientId = await addClientToDB(values);
      }

      if (!appointment?.id) {
        return;
      }

      const appointmentData = {
        clientId,
        note: values.note,
        mark: values.mark?.value,
        model: values.model,
        year: values.year?.value,
      };

      await UserService.addAppointment(appointment.id, appointmentData);
      onAppointmentChange();

      setTimeout(() => {
        alert("Appointment set!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitAppointment = async (
    values: AppointmentFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    if (isFreeAppointment) {
      await addAppointmentForClient(values, setSubmitting);
      return;
    }

    await editAppointmentData(values, setSubmitting);
    setIsEditDisabled(true);
  };

  const askAddPayment = async () => {
    if (
      await ConfirmationBox(
        `Are your sure to confirm payment for client ${appointment?.client?.clientName ?? ""} with appointment at ${formatDate()}?`,
        "OK",
        "Cancel",
        {
          title: "Confirm payment",
        },
      )
    ) {
      await addPayment();
    }
  };

  const addPayment = async () => {
    if (!appointment?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await UserService.addPayment(appointment.id);
      onAppointmentChange();
      setTimeout(() => {
        alert("Payment added!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const askCancelAppointment = async () => {
    if (
      await ConfirmationBox(
        `Are your sure to cancel appointment at ${formatDate()} for client ${appointment?.client?.clientName ?? ""}?`,
        "OK",
        "Cancel",
        {
          title: "Cancel",
        },
      )
    ) {
      await cancelAppointment("true");
    }
  };

  const askCancelAppointmentNoRefund = async () => {
    if (
      await ConfirmationBox(
        `Are your sure to cancel appointment WITHOUT REFUND at ${formatDate()} for client ${appointment?.client?.clientName ?? ""}?`,
        "OK",
        "Cancel",
        {
          title: "Cancel",
        },
      )
    ) {
      await cancelAppointment("false");
    }
  };

  const cancelAppointment = async (refund: "true" | "false") => {
    if (!appointment?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        withRefund: refund,
      };
      await UserService.cancelAppointment(appointment.id, data);
      onAppointmentChange();
      setTimeout(() => {
        alert("Appointment canceled!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const askAppointmentFinished = async () => {
    if (
      await ConfirmationBox(
        `Are your sure to confirm finishing appointment for client ${appointment?.client?.clientName ?? ""}, date ${formatDate()}?`,
        "OK",
        "Cancel",
        {
          title: "Appointment finished!",
        },
      )
    ) {
      await appointmentFinished();
    }
  };

  const appointmentFinished = async () => {
    if (!appointment?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await UserService.appointmentComplete(appointment.id);
      onAppointmentChange();
      setTimeout(() => {
        alert("Appointment completed!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
      console.log(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const askClientDoNotShowUp = async () => {
    if (
      await ConfirmationBox(
        `Are your sure to set up appointment ${formatDate()} as client ${appointment?.client?.clientName ?? ""} do not show up?`,
        "OK",
        "Cancel",
        {
          title: "Client do not show up!",
        },
      )
    ) {
      await clientDoNotShowUp();
    }
  };

  const clientDoNotShowUp = async () => {
    if (!appointment?.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await UserService.appointmentMissed(appointment.id);
      onAppointmentChange();
      setTimeout(() => {
        alert("Set as client not show up!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatus = (appointmentItem: AppointmentRecord) => {
    switch (appointmentItem.status) {
      case "draft":
        return "New appointment";
      case "day_off":
        return `This Appointment is set as Day Off because of ${appointmentItem.note}, but you still could add appointment on it`;
      case "added":
        return "Appointment added";
      case "confirmed":
        return "Appointment confirmed";
      case "paid":
        return "Appointment paid";
      case "done":
        return "Appointment done";
      case "client_not_show_up":
        return "Client not show up";
      default:
        return "Appointment";
    }
  };

  const enableEdit = () => {
    setIsEditDisabled(false);
  };

  const saveClientData = (values: AppointmentFormValues) => {
    editClient(values);
    setIsEditDisabled(true);
  }

  const enableEditDate = () => {
    setEditDate(true);
  };

  const disableEditDate = () => {
    setEditDate(false);
  };

  const newDateChoosen = async (newAppointment: AppointmentRecord) => {
    if (!appointment?.id || !newAppointment.id) {
      return;
    }

    setIsSubmitting(true);
    setEditDate(false);
    try {
      const data = {
        newAppointment: newAppointment.id,
      };
      await UserService.changeAppointmentDate(appointment.id, data);
      setTimeout(() => {
        alert("Appointment date changed!");
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const showButtonWithoutRefund = (appointmentItem: AppointmentRecord) => {
    if (appointmentItem.status !== "paid") {
      return false;
    }

    const start = new Date(appointmentItem.startDateTime).getTime();
    const diff = Math.floor((start - Date.now()) / (1000 * 60 * 60 * 48));
    return diff < 1;
  };

  const EditAppointmentButtons = () => {
    if (
      appointment &&
      statusesAllowedToCancel.includes(appointment.status) &&
      isEditDisabled
    ) {
      return (
        <div style={stylingObject.topButtonsDiv}>
          <Button
            style={stylingObject.buttonLeft}
            variant="info"
            type="button"
            onClick={enableEdit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <span>Edit Client Data</span>
            )}
          </Button>
          <Button
            style={stylingObject.buttonEditDate}
            variant="info"
            type="button"
            onClick={enableEditDate}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <span>Change appointment date</span>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  const getBackgroundColor = (appointmentItem: AppointmentRecord | null) =>
    appointmentItem?.status === "day_off" ? "#ffc107" : "#FFF";

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: getBackgroundColor(appointment) }}
        >
          <Modal.Title id="contained-modal-title-vcenter">
            {appointment && (
              <h4>
                {getStatus(appointment)}: {formatDate()}
              </h4>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditAppointmentButtons />
          {appointment && appointment.status === "draft" && (
            <DayOff
              appointment={appointment as AppointmentItem}
              onAppointmentChange={onAppointmentChange}
              onHide={onHide}
            />
          )}
          {appointment && appointment.status === "day_off" && (
            <DayOffRemove
              appointment={appointment as AppointmentItem}
              onAppointmentChange={onAppointmentChange}
              onHide={onHide}
            />
          )}
          <Formik<AppointmentFormValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              await submitAppointment(values, setSubmitting);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting: isFormSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => (
              <form onSubmit={handleSubmit} style={stylingObject.form}>
                <ClientSelectionFields
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  isFormDisabled={isEditDisabled}
                  showAutocomplete={!isEditDisabled && isFreeAppointment}
                  showDetailsHeading={!isEditDisabled && isFreeAppointment}
                  initialClientLocked={Boolean(values.client) && isEditDisabled}
                  autocompletePlaceholder="Select client"
                />
                {appointment && appointment.status !== "draft" && !isEditDisabled && (
                  <Button
                    style={stylingObject.buttonLeft}
                    variant="danger"
                    type="button"
                    onClick={() => saveClientData(values)}
                  >
                    Save Client Data
                  </Button>
                )}
                <h6 style={stylingObject.labels}>Select motorcycle year</h6>
                <Field
                  component={Select}
                  options={productionYears}
                  name="year"
                  onChange={(value: SelectOption<number | string> | null) => {
                    setFieldValue("year", value);
                  }}
                  onBlur={handleBlur}
                  value={values.year}
                  isDisabled={isEditDisabled}
                />
                <p style={stylingObject.errorLabel}>
                  {getFieldError(errors.year, touched.year)}
                </p>
                <h6 style={stylingObject.labels}>Select motorcycle made</h6>
                <Field
                  component={Select}
                  options={madeList}
                  name="mark"
                  onChange={(value: SelectOption<string> | null) => {
                    setFieldValue("mark", value);
                  }}
                  onBlur={handleBlur}
                  value={values.mark}
                  isDisabled={isEditDisabled}
                />
                <p style={stylingObject.errorLabel}>
                  {getFieldError(errors.mark, touched.mark)}
                </p>
                <h6 style={stylingObject.labels}>Enter motorcycle model</h6>
                <input
                  name="model"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.model}
                  disabled={isEditDisabled}
                />
                <p style={stylingObject.errorLabel}>
                  {getFieldError(errors.model, touched.model)}
                </p>
                <h6 style={stylingObject.labels}>Notes</h6>
                <input
                  name="note"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.note}
                  disabled={isEditDisabled}
                />
                <p style={stylingObject.errorLabel}>
                  {getFieldError(errors.note, touched.note)}
                </p>
                <div style={stylingObject.buttonsDiv}>
                  {!isEditDisabled && isFreeAppointment && (
                    <Button
                      type="submit"
                      style={stylingObject.buttonRight}
                      disabled={isFormSubmitting}
                    >
                      {isFormSubmitting ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <span>Save and send info to client</span>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            )}
          </Formik>
          <div style={stylingObject.buttonsDiv}>
            {appointment && statusesAllowedToCancel.includes(appointment.status) && (
              <div style={stylingObject.cancelButtonsFlex}>
                <Button
                  type="button"
                  variant="danger"
                  onClick={askCancelAppointment}
                >
                  {isSubmitting ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span>
                      Cancel Appointment {showButtonWithoutRefund(appointment) && <span>WITH REFUND</span>}
                    </span>
                  )}
                </Button>
                {showButtonWithoutRefund(appointment) && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={askCancelAppointmentNoRefund}
                    style={{ marginTop: 10 }}
                  >
                    {isSubmitting ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <span>Cancel Appointment WITHOUT REFUND</span>
                    )}
                  </Button>
                )}
              </div>
            )}
            {appointment && appointment.status === "paid" && (
              <Button
                style={stylingObject.buttonRight}
                variant="success"
                type="button"
                onClick={askAppointmentFinished}
              >
                {isSubmitting ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Service Done</span>
                )}
              </Button>
            )}
            {appointment && appointment.status === "added" && (
              <Button
                style={stylingObject.buttonRight}
                type="button"
                onClick={askAddPayment}
              >
                {isSubmitting ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Confirm client payment</span>
                )}
              </Button>
            )}
            {appointment && appointment.status === "paid" && (
              <Button
                style={stylingObject.buttonRight}
                variant="dark"
                type="button"
                onClick={askClientDoNotShowUp}
              >
                {isSubmitting ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Client do not show up</span>
                )}
              </Button>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <FreeAppointments
        show={editDate}
        onHide={disableEditDate}
        appointment={appointment as AppointmentItem | null}
        newDateChoosen={newDateChoosen}
      />
    </>
  );
};

const stylingObject = {
  form: {
    display: "flex",
    flexDirection: "column",
  },
  topButtonsDiv: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
  },
  buttonsDiv: {
    display: "flex",
    flexDirection: "row",
    paddingTop: 10,
    gap: 10,
  },
  labels: {
    paddingTop: ".7rem",
    marginBottom: ".1rem",
  },
  buttonRight: {
    marginLeft: "auto",
  },
  buttonLeft: {
    marginRight: "auto",
  },
  buttonEditDate: {
    marginRight: "auto",
    marginLeft: 10,
  },
  customUI: {
    textAlign: "center",
    color: "white",
    zIndex: 99999,
    position: "fixed",
  },
  errorLabel: {
    color: "red",
  },
  cancelButtonsFlex: {
    display: "flex",
    flexDirection: "column",
  },
  phoneNumberLink: {
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "0.1rem",
    padding: "2px",
    textDecoration: "none",
  },
} satisfies Record<
  | "form"
  | "topButtonsDiv"
  | "buttonsDiv"
  | "labels"
  | "buttonRight"
  | "buttonLeft"
  | "buttonEditDate"
  | "customUI"
  | "errorLabel"
  | "cancelButtonsFlex"
  | "phoneNumberLink",
  React.CSSProperties
>;

export default Appointment;
